import { Alert, Anchor, Badge, Box, Code, Group, Table, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';

const SETTINGS = { label: 'Domain settings', path: '/settings' };
const PERSONALIZATION = { label: 'Personalization', path: '/chatBot?tab=personalization' };
const BRAND_CONTEXT = { label: 'Brand Identity', path: '/brandContext' };

const FIELDS = [
  { key: 'brandName', label: 'Brand name', setIn: SETTINGS },
  { key: 'responseLocale', label: 'Response language' },
  { key: 'catalogLocale', label: 'Catalog language' },
  { key: 'currency', label: 'Currency', setIn: SETTINGS },
  { key: 'supportEmail', label: 'Support email', setIn: PERSONALIZATION },
  { key: 'supportMessage', label: 'Fallback message', setIn: PERSONALIZATION },
  { key: 'voice', label: 'Voice', setIn: BRAND_CONTEXT },
  { key: 'tone', label: 'Tone', setIn: BRAND_CONTEXT },
  { key: 'documentsNumber', label: 'Documents in Store section' },
  { key: 'productsNumber', label: 'Products per search' },
  { key: 'multiLocaleProducts', label: 'Multi-locale products' },
];

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'not set';
  }
  if (typeof value === 'boolean') {
    return value ? 'yes' : 'no';
  }
  return String(value);
};

export const fieldRows = (tenantBlock, defaults) =>
  FIELDS.map(({ key, label, setIn }) => ({
    key,
    label,
    value: displayValue(tenantBlock?.[key]),
    isDefault: (defaults || []).includes(key),
    setIn: setIn || null,
  }));

const StoreSectionPreview = ({ fetchUrl, accountId, domainId, chatBotChanged, refreshKey }) => {
  const fetch = useFetch();

  const [preview, setPreview] = useState();
  const [error, setError] = useState();

  useEffect(() => {
    // A save bumps refreshKey while the previous read may still be in flight; the
    // older block must not land on top of the one that reflects the saved row.
    let live = true;
    setPreview(undefined);
    setError(undefined);

    fetch(`${fetchUrl}/chatBot/tenantBlock`)
      .then((res) => {
        if (live) {
          setPreview(res);
        }
      })
      .catch((err) => {
        if (live) {
          setError(getErrorMessage(err, 'Could not load the Store section.'));
        }
      });

    return () => {
      live = false;
    };
  }, [fetch, fetchUrl, refreshKey]);

  if (error) {
    return <ErrorMessage message={error} />;
  }

  if (!preview) {
    return <Spinner />;
  }

  const domainPath = `/accounts/${accountId}/domains/${domainId}`;

  return (
    <Box>
      <Group justify="space-between" gap="sm" wrap="nowrap">
        <Text size="xs" c="dimmed">
          This is the Store section the assistant receives after its tools, from the saved settings
        </Text>
        {preview.agentProfile && (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {preview.agentProfile.id} v{preview.agentProfile.version}
          </Text>
        )}
      </Group>

      {chatBotChanged && (
        <Alert color="warning" variant="light" mt="xs" py={6}>
          Unsaved changes are not reflected until you save
        </Alert>
      )}

      <Code block mt="sm" fz={12} style={{ whiteSpace: 'pre-wrap' }}>
        {preview.rendered}
      </Code>

      {/* Date, page and location change every turn, so they travel after the cached Store section. */}
      {preview.perRun?.length > 0 && (
        <Group gap={6} mt="xs">
          <Text size="xs" c="dimmed">
            Added on every run, not cached:
          </Text>
          {preview.perRun.map((line) => (
            <Badge key={line} size="xs" variant="light" color="gray" tt="none">
              {line} · per run
            </Badge>
          ))}
        </Group>
      )}

      <Table mt="md" fz="xs" verticalSpacing={4}>
        <Table.Tbody>
          {fieldRows(preview.tenantBlock, preview.defaults).map((row) => (
            <Table.Tr key={row.key}>
              <Table.Td w={200} c="dimmed">
                {row.label}
              </Table.Td>
              <Table.Td style={{ wordBreak: 'break-word' }}>{row.value}</Table.Td>
              <Table.Td w={220}>
                {row.isDefault && (
                  <Group gap={6} wrap="nowrap">
                    <Badge size="xs" variant="light" color="gray">
                      default
                    </Badge>
                    {row.setIn && (
                      <Anchor component={Link} to={`${domainPath}${row.setIn.path}`} fz="xs">
                        {row.setIn.label}
                      </Anchor>
                    )}
                  </Group>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

export default StoreSectionPreview;
